import { useEffect, useState } from 'react'
import { Form, useNavigate } from 'react-router-dom';
import { Alert, Button, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import { IBatchPayeeOption } from '../../types/shipment';
import { ISelectOption } from '../../types/list';

import { BatchService } from '../../services/batchService';

import Spinner from '../spinner';

import '../../styles/entity/batchForms.scss';

export interface IFormFields {
	id?: number;
	invoiceNumber: string;
	selectedPayee: string;
	payeeType: string;
	payeeId: string;
	month: string; // Format: YYYYMM
	status: string;
}

const BatchAddForm = () => {
	const navigate = useNavigate();
	const [formFields, setFormFields] = useState<IFormFields>({
		invoiceNumber: "",
		month: "-1",
		selectedPayee: "-1",
		payeeId: "",
		payeeType: "",
		status: "Reconciling"
	});
	const monthOptions: ISelectOption[] = [
		{ name: '01 - January', value: '01' },
		{ name: '02 - February', value: '02' },
		{ name: '03 - March', value: '03' },
		{ name: '04 - April', value: '04' },
		{ name: '05 - May', value: '05' },
		{ name: '06 - June', value: '06' },
		{ name: '07 - July', value: '07' },
		{ name: '08 - August', value: '08' },
		{ name: '09 - September', value: '09' },
		{ name: '10 - October', value: '10' },
		{ name: '11 - November', value: '11' },
		{ name: '12 - December', value: '12' },
	];
	const [yearOptions, setYearOptions] = useState<ISelectOption[]>([]);
	const [payeeOptions, setPayeeOptions] = useState<IBatchPayeeOption[]>([]);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const [selectedMonthOption, setSelectedMonthOption] = useState<string>('-1');
	const [selectedYearOption, setSelectedYearOption] = useState<string>('-1');

	const [isLoadingDateOptions, setIsLoadingDateOptions] = useState(true);
	const [isLoadingPayees, setIsLoadingPayees] = useState(true);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		loadDateFields();
	}, [])

	useEffect(() => {
		if (!isLoadingDateOptions && !isLoadingPayees) {

			// Don't allow setting a date in the future
			var newDate = dayjs().month(parseInt(selectedMonthOption) - 1).year(parseInt(selectedYearOption));
			if (newDate > dayjs()) {
				const safeMonth = dayjs().format('MM');
				const safeYear = dayjs().format('YYYY');
				setSelectedMonthOption(dayjs().format('MM'));
				setSelectedYearOption(dayjs().format('YYYY'));
				setFormFields({ ...formFields, ['month']: safeYear + safeMonth });
			}
			else {
				setFormFields({ ...formFields, ['month']: selectedYearOption + selectedMonthOption });
			}
		}
	}, [selectedMonthOption, selectedYearOption, isLoadingPayees])

	useEffect(() => {
		if (!isLoadingDateOptions) {
			// Wait for loadDateFields() to finish so both load methods aren't competing to overwrite each other's formFields
			loadPayees();
		}
	}, [isLoadingDateOptions])

	const loadDateFields = async () => {
		const startYear = 2025;
		const currentYear = parseInt(dayjs().format('YYYY'));
		const years: ISelectOption[] = [];
		for (let year = startYear; year <= currentYear; year++) {
			years.push({ name: year.toString(), value: year.toString() });
		}
		setYearOptions(years);

		// Default date is last month
		const selectedDate = dayjs().subtract(1, 'months');

		setSelectedMonthOption(selectedDate.format('MM'));
		setSelectedYearOption(selectedDate.format('YYYY'));
		setIsLoadingDateOptions(false);
	}

	const loadPayees = async () => {
		setIsLoadingPayees(true);
		BatchService.getPayees()
			.then((response) => {
				console.log('BatchService.getPayees: SUCCESS', response);
				let options: IBatchPayeeOption[] = [];
				response.data.map((item, index) => {
					let option: IBatchPayeeOption = {
						localId: index,
						entityId: item.id,
						entityName: item.name,
						entityType: item.type
					}

					options.push(option);
				})
				setPayeeOptions(options);
				if (options.length > 0) {
					let newFormFields = { ...formFields };
					newFormFields.selectedPayee = options[0].localId.toString();
					newFormFields.payeeId = options[0].entityId.toString();
					newFormFields.payeeType = options[0].entityType;
					setFormFields(newFormFields);
				}
			})
			.catch((error) => {
				console.log('BatchService.getPayees: ERROR', error);
				setErrorMessage("We encountered an error while loading payees. Please try again.");
			})
			.finally(() => {
				setIsLoadingPayees(false);
			})
	}

	const setPayeeInfo = (selection: string) => {
		let newFormFields: IFormFields = { ...formFields };
		let selectedPayee = payeeOptions.filter((item) => { return item.localId.toString() == selection ? true : false })

		if (selectedPayee && selectedPayee.length > 0) {
			newFormFields.selectedPayee = selection;
			newFormFields.payeeId = selectedPayee[0].entityId.toString();
			newFormFields.payeeType = selectedPayee[0].entityType;
		}

		setFormFields(newFormFields);
	}

	const handleSubmit = async () => {
		setErrorMessage(undefined);
		if (formFields.invoiceNumber.trim() === '' || selectedMonthOption === '-1' || selectedYearOption === '-1' || formFields.selectedPayee === '-1') {
			setErrorMessage("Please enter values for all fields to continue.");
			return;
		}

		setIsSaving(true);
		const { selectedPayee, ...batch } = formFields;
		BatchService.addBatch(batch)
			.then((response) => {
				console.log('BatchService.addBatch: SUCCESS', response);
				navigate('/batch/' + response.data.id);
			})
			.catch((error) => {
				console.log('BatchService.addBatch: ERROR', error);
				setErrorMessage("We encountered an error while saving the batch. Please try again.");
			})
			.finally(() => {
				setIsSaving(false);
			});
	}

	return (
		<section className="hwi-batch-add-form">
			<Spinner dependency={isLoadingPayees} text='Loading Payees...'>
				{errorMessage &&
					<Alert className="alert-error"
						severity="error"
						action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
						{errorMessage}
					</Alert>
				}
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Form onSubmit={() => handleSubmit()}>
						<div className="row">
							<InputLabel>Invoice Number *</InputLabel>
							<TextField
								className="invoice-field"
								required variant="outlined"
								value={formFields ? formFields.invoiceNumber : ''}
								size="small"
								inputProps={{ maxLength: 50 }}
								autoFocus
								onChange={(event) => { setFormFields({ ...formFields, ["invoiceNumber"]: event.target.value }) }}
							>
							</TextField>

						</div>
						<div className="row">
							<InputLabel>Payee *</InputLabel>
							<Select
								className="payee-field"
								value={formFields ? formFields.selectedPayee : '-1'}
								required
								fullWidth
								onChange={(event) => { setPayeeInfo(event.target.value) }}
							>
								<MenuItem value="-1" key={0} disabled>Select one...</MenuItem>
								{payeeOptions.map((item, index) => (
									<MenuItem value={item.localId} key={index} >{item.entityName} ({item.entityType})</MenuItem>
								))}
							</Select>
						</div>
						<div className="row">
							<InputLabel>Month *</InputLabel>
							<div className="date-field-container">
								<Select
									className="month-field"
									value={selectedMonthOption ?? '-1'}
									required
									fullWidth
									onChange={(event) => { setSelectedMonthOption(event.target.value) }}
								>
									<MenuItem value="-1" key={0} disabled>Select one...</MenuItem>
									{monthOptions.map((item, index) => (
										<MenuItem value={item.value} key={index} >{item.name}</MenuItem>
									))}
								</Select>
								<Select
									className="year-field"
									value={selectedYearOption ?? '-1'}
									required
									fullWidth
									onChange={(event) => { setSelectedYearOption(event.target.value) }}
								>
									<MenuItem value="-1" key={0} disabled>Select one...</MenuItem>
									{yearOptions.map((item, index) => (
										<MenuItem value={item.value} key={index} >{item.name}</MenuItem>
									))}
								</Select>
							</div>
						</div>
						<div className="row controls">
							<Button type="submit" className="save-button" variant="contained" disabled={isSaving || formFields.selectedPayee == "-1" || formFields.month == "-1" || formFields.invoiceNumber == ""}>{isSaving ? "Saving..." : "Save"}</Button>
						</div>
						{/* <Json data={formFields} title='Form Fields'></Json> */}
					</Form>
				</LocalizationProvider>
			</Spinner>
		</section>
	)
}

export default BatchAddForm;